/* eslint-disable no-underscore-dangle */
import { ApiError, ApiErrorCode, BaseAPI, DATA_RANGE_HEADER_KEY, parseDataRange, RequestMethod, createFormData, } from '@moderepo/mode-apis-base';
import { XSRF_GUARD_KEY, } from './models';
/**
 * These are the API used for MODE customer support apps
 */
export class RitzAPI extends BaseAPI {
    /**
     * There is no need to create multiple instances of RitzAPI so we will make the constructor
     * private to prevent the dev from creating instance of this class
     */
    constructor(apiBaseUrl, xsrfGuard) {
        super({
            baseURL: apiBaseUrl,
            withCredentials: true,
        });
        this.xsrfGuard = xsrfGuard;
    }
    // Initialize RitzAPI. If a RitzAPI instance has NOT been created, we will create a new instance and initialize it with the provided
    // params. If an instance already existed and if xsrfGuard is provided, we will update the current instance's xsrfGuard
    static initialize(baseUrl, xsrfGuard) {
        if (!RitzAPI.instance) {
            RitzAPI.instance = new RitzAPI(baseUrl, xsrfGuard);
        }
        else if (xsrfGuard) {
            RitzAPI.instance.setXsrfGuard(xsrfGuard);
        }
    }
    /**
     * Return an Instance of RitzAPI. This is what dev should be using to get an instance of RitzAPI
     * and use it to make API call.
     */
    static getInstance() {
        if (!RitzAPI.instance) {
            // This error will stop the app so we can just use the default Error type
            throw new Error('RitzAPI.initialize must be called before it can be used.');
        }
        return RitzAPI.instance;
    }
    /**
     * Set the agent's login xsrfGuard
     */
    setXsrfGuard(xsrfGuard) {
        this.xsrfGuard = xsrfGuard;
    }
    /**
     * Return the agent's login info if the agent is logged in
     */
    getXsrfGuard() {
        return this.xsrfGuard;
    }
    /**
     * Clear the agent's login info
     */
    clearXsrfGuard() {
        this.xsrfGuard = undefined;
    }
    /**
     * Get the organization info for the specified orgId
     * @param orgId
     * @returns
     */
    async getOrganizationById(orgId) {
        const response = await this.sendRequest(RequestMethod.GET, `/organizations/${orgId}`);
        return response.data;
    }
    /**
     * Get the organization info by baseURL
     * @param orgId
     * @returns
     */
    async getOrganizationByBaseUrl(baseUrl) {
        const response = await this.sendRequest(RequestMethod.GET, '/organizations/', {
            opsConsoleBaseUrl: baseUrl,
        });
        return response.data;
    }
    /**
     * Activate an Agent. This is used for activating a NEW agent account, an account that has not set up name and password yet
     */
    async activateAgent(orgId, name, password, token) {
        const response = await this.sendFormRequest(RequestMethod.POST, '/auth/activation', createFormData({
            orgId, token, name, password,
        }));
        return this.handleLoginResponse(response);
    }
    /**
     * Verify an agent. This is used for when an agent changed his password.
     */
    async verifyAgent(token) {
        const response = await this.sendFormRequest(RequestMethod.POST, '/auth/verification', createFormData({
            token,
        }));
        return this.handleLoginResponse(response);
    }
    /**
     * Log the agent in with the provided email/password.
     */
    async login(orgId, email, password) {
        const response = await this.sendFormRequest(RequestMethod.POST, '/auth/login', createFormData({
            orgId, email, password,
        }));
        if (response && response.data && response.data.authenticated) {
            return this.handleLoginResponse(response);
        }
        // If agent has 2FA enabled, this call will be successful BUT it will not contain the authenticated login info.
        // It will return a token instead which we need to make another call to verify the token and authentication code.
        return {
            authenticated: response.data.authenticated,
            agentId: response.data.agentId,
            auth2FToken: response.data.auth2FToken,
        };
    }
    /**
     * Log the agent in with 2FA token and authentication code
     */
    async loginWith2FA(token, code) {
        const response = await this.sendFormRequest(RequestMethod.POST, '/auth/auth2F', createFormData({
            token, code: String(code),
        }));
        return this.handleLoginResponse(response);
    }
    /**
     * Get authentication URL for external login service
     */
    async getExternalAuthURL(orgId, providerId) {
        const response = await this.sendRequest(RequestMethod.POST, '/auth/oidc/start', {
            orgId, providerId,
        });
        return response.data;
    }
    /**
     * Authenticate the agent with the response code/state received from the external login service
     */
    async authAgentWithExternalLogin(orgId, providerId, code, state) {
        const response = await this.sendRequest(RequestMethod.POST, '/auth/oidc/finalize', {
            orgId, providerId, code, state,
        });
        return this.handleExternalLoginResponse(response);
    }
    /**
     * Send a request to reset the agent's password. This is used for agent who forgot their password.
     * Note that this will only send a request, not change the agent's password.
     */
    async requestResetPassword(orgId, email) {
        await this.sendFormRequest(RequestMethod.POST, '/auth/startPasswordReset', createFormData({
            orgId, email,
        }));
        return Promise.resolve(true);
    }
    /**
     * Reset the agent's password. This should only be called AFTER the agent has requested for resetting password
     * and received an email with the token to reset password.
     */
    async resetPassword(orgId, token, password) {
        const response = await this.sendFormRequest(RequestMethod.POST, '/auth/passwordReset', createFormData({
            orgId, token, newPassword: password,
        }));
        // If agent has 2FA enabled, this call will be successful BUT it will not contain the authenticated login info.
        // It will return a token instead which we need to make another call to verify the token and authentication code.
        if (response && response.data && response.data.authenticated) {
            return this.handleLoginResponse(response);
        }
        return response.data;
    }
    /**
     * Activate 2FA for agent login account. This is only the first step to activate agent's account for 2FA.
     * The agent must complete the activate2FAComplete step to actually activate the account for using 2FA
     */
    async activate2FAStart(password) {
        const response = await this.sendRequest(RequestMethod.POST, '/auth/activateAuth2F', {
            password,
        });
        return response.data;
    }
    /**
     * Verify 2FA activation. This will be the final step that actually active the agent account
     */
    async activate2FAComplete(code) {
        const response = await this.sendRequest(RequestMethod.POST, '/auth/verifyAuth2F', {
            code: String(code),
        });
        return response.data;
    }
    /**
     * Deactivate 2FA
     */
    async deactivate2FA(password) {
        await this.sendRequest(RequestMethod.POST, '/auth/deactivateAuth2F', {
            password,
        });
    }
    /**
     * Get an Agent's profile data for the given agent id.
     * @param agentId - number - The id of the agent whose profile is to be loaded
     */
    async getAgentProfile(agentId) {
        const response = await this.sendRequest(RequestMethod.GET, `/agents/${agentId}`);
        return response.data;
    }
    /**
     * This is for updating the LOGGED IN agent's personal profile.
     */
    async updateAgentPersonalProfile(agentId, { name, email, preferences }) {
        await this.sendRequest(RequestMethod.PATCH, `/agents/${agentId}`, {
            name,
            email,
            preferences,
        });
    }
    /**
     * Change the logged in agent's password
     * @param orgId
     * @param email
     * @param oldPassword - Can be undefined if the agent does not have password set
     * @param newPassword
     */
    async updateAgentPersonalPassword(orgId, email, oldPassword, newPassword) {
        await this.sendRequest(RequestMethod.POST, '/auth/passwordChange', {
            orgId,
            email,
            oldPassword,
            newPassword,
        });
    }
    /**
     * Log the agent out
     */
    async logout() {
        await this.sendRequest(RequestMethod.DELETE, '/auth');
        this.clearXsrfGuard();
        return Promise.resolve();
    }
    /**
     * Get Application's settings
     */
    async getAppSettings() {
        const response = await this.sendRequest(RequestMethod.GET, '/projectOpts');
        return {
            supportedThemes: response.data.themes,
            supportedProjectTypes: response.data.types,
        };
    }
    /**
     * Get an agent's list of projects that he has access to.
     *
     * @param agentId - The id of the agent whose project list is to be loaded for,
     */
    async getAgentProjects(agentId) {
        const response = await this.sendRequest(RequestMethod.GET, '/projects', {
            agentId,
        });
        return response.data;
    }
    /**
     * Get an agent's project by id
     *
     * @param agentId - The id of the agent whose project list is to be loaded for,
     * @param projectId - The id of the project
     */
    async getAgentProjectById(agentId, projectId) {
        const response = await this.sendRequest(RequestMethod.GET, `/projects/${projectId}`, {
            agentId,
        });
        return response.data;
    }
    /**
     * Generate a Super key that the agent can use to access all project's info e.g. device classes, tsdb, etc...
     * @param projectId
     */
    async generateProjectKey(projectId) {
        const response = await this.sendRequest(RequestMethod.POST, `/projects/${projectId}/generateProjectKey`);
        const PROJECT_KEY_EXPIRATION = 3 * 60 * 60 * 1000;
        // Token expires in 3 hours. However, backend returns only the token for now so we need to add expirationTime time to the data
        // TODO - Have backend return expirationTime instead
        return {
            token: response.data.token,
            expirationTime: (new Date(Date.now() + PROJECT_KEY_EXPIRATION)).toISOString(),
        };
    }
    /**
     * Get the statistics for a project
     * @param projectId - The id of the project
     */
    async getProjectStatistics(projectId) {
        const response = await this.sendRequest(RequestMethod.GET, `/projects/${projectId}/stats`);
        return response.data;
    }
    async updateProjectSettings(projectId, settings) {
        await this.sendRequest(RequestMethod.PATCH, `/projects/${projectId}`, {
            ...settings,
        });
    }
    /**
     * Get data for an agent in a specific project
     * @param projectId - The id of the project
     * @param agentId - The id of the agent whose data should be loaded
     */
    async getProjectAgent(projectId, agentId) {
        const response = await this.sendRequest(RequestMethod.GET, `/projects/${projectId}/agents/${agentId}`);
        // data returned by backend does not include projectId so we need to do it here
        return {
            ...response.data, projectId,
        };
    }
    /**
     * Get data for ALL agents in a project
     * @param projectId - The id of the project
     */
    async getAllProjectAgents(projectId) {
        const response = await this.sendRequest(RequestMethod.GET, `/projects/${projectId}/agents`);
        // data returned by backend does not include projectId so we need to do it here
        return response.data.map((data) => {
            return {
                ...data,
                projectId,
            };
        });
    }
    /**
     * Create/invite an agent to a project
     * @param projectId - The id of the project
     * @param email - The email of the agent to be invited
     * @param role - The role of the agent to be invited
     */
    async createAgent(projectId, email, role) {
        const response = await this.sendRequest(RequestMethod.POST, `/projects/${projectId}/agents`, {
            email, role,
        });
        return response.data;
    }
    /**
     * Remove an agent from a project
     * @param projectId - The id of the project
     * @param agentId - The id of the agent to be removed from the project
     */
    async removeAgent(projectId, agentId) {
        await this.sendRequest(RequestMethod.DELETE, `/projects/${projectId}/agents/${agentId}`);
    }
    /**
     * Resend activation email to a specified agent
     * @param projectId - The id of the project
     * @param agentId - The id of the agent to send to
     * @param email - The email of the agent to send to
     */
    async resendActivateEmail(projectId, agentId, email) {
        await this.sendRequest(RequestMethod.POST, `/projects/${projectId}/agents/${agentId}/resendActivation`, {
            email,
        });
    }
    /**
     * Update an agent's role
     * @param projectId - The id of the project
     * @param agentId - The id of the agent to be updated
     * @param role - The agent's new role
     */
    async updateAgentRole(projectId, agentId, role) {
        await this.sendRequest(RequestMethod.PATCH, `/projects/${projectId}/agents/${agentId}`, {
            role,
        });
    }
    /**
     * Get all users for the specified project with the specified filters
     * @param projectId - The project id
     * @param search - Filter by the specified 'search' string. Return only users who have email, name, or phone
     *                 that matches the 'search' string
     * @param skip - The number of users to skip, the index of the first user to return
     * @param limit - The maximum # of users to return if there are more than max
     */
    async getFilteredUsers(projectId, search, skip, limit) {
        const response = await this.sendRequest(RequestMethod.GET, '/users', {
            projectId, search, skip, limit,
        });
        if (response.data instanceof Array) {
            return {
                range: parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]),
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get a user profile by his user id and return a PaginationDataSet for that result.
     * We don't have an API to search user by id that does similar thing like the getFilteredUsers. However, we have a getUserById
     * but it is not the same. It return a User object instead of an Array of users. Also, it will throw an error if user is not found
     * instead of returning an empty list. So, we will use the getUserById to simulate a filter user by id API.
     *
     * @param projectId - The id of the project the user belonged to
     * @param userId - The id of the user to look for
     */
    async getFilteredUser(projectId, userId) {
        try {
            const response = await this.sendRequest(RequestMethod.GET, `/users/${userId}`, {
                projectId,
            });
            const user = response.data;
            if (user.projectId === projectId) {
                // Backend does not check projectId so it will return the user if the ID match.
                // We need to filter out result that does not belong to the current project
                return {
                    range: {
                        start: 0,
                        end: 1,
                        total: 1,
                    },
                    items: [user],
                };
            }
        }
        catch (error) {
            // ignore error
        }
        // return empty data set
        return {
            items: [],
        };
    }
    /**
     * Get a user profile by his user id
     * @param projectId - The id of the project the user belonged to
     * @param userId - The id of the user to look for
     */
    async getUserById(projectId, userId) {
        const response = await this.sendRequest(RequestMethod.GET, `/users/${userId}`, {
            projectId,
        });
        return response.data;
    }
    /**
     * Create a new user with email as login
     * @param projectId - The id of the project which the user to be created for
     * @param name - The new user name
     * @param email - The new user email
     * @param password - The new user password
     */
    async createUserByEmail(projectId, name, email, password) {
        const response = await this.sendRequest(RequestMethod.POST, '/users', {
            projectId, name, email, password,
        });
        return response.data;
    }
    /**
     * Create a new user with phone number as login
     * @param projectId - The id of the project which the user to be created for
     * @param name - The new user name
     * @param phone - The new user phone number
     */
    async createUserByPhone(projectId, name, phoneNumber) {
        const response = await this.sendRequest(RequestMethod.POST, '/users', {
            projectId, name, phoneNumber,
        });
        return response.data;
    }
    /**
     * Get user key used for making API call on user behalf
     * @param projectId - The id of the project the user belonged to
     * @param userId - The cid of the user to generate key for
     */
    async createUserKey(projectId, userId) {
        const response = await this.sendRequest(RequestMethod.POST, `/users/${userId}/generateKey`, {
            projectId,
        });
        // Add user ID into the userKey data because backend does not includes it. We need the user ID in the key
        // so that we can easily tell which user this key belonged to.
        return {
            ...response.data, userId,
        };
    }
    /**
     * Update the user profile, only user name for now
     * @param userId - The ID of the user we want to update
     * @param name - The name to set for the user
     */
    async updateUser(userId, name) {
        await this.sendRequest(RequestMethod.PATCH, `/users/${userId}`, {
            name,
        });
    }
    /**
     * Get all homes belonged to a project
     * @param projectId - The id of the user to look for
     */
    async getProjectHomes(projectId, skip, limit) {
        const response = await this.sendRequest(RequestMethod.GET, '/homes', {
            projectId, skip, limit,
        });
        if (response.data instanceof Array) {
            const range = parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]);
            return {
                range,
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get the list of device classes for a given project
     * @param projectId - The id of the project the device classes belonged to
     */
    async getDeviceClasses(projectId) {
        const response = await this.sendRequest(RequestMethod.GET, `/projects/${projectId}/deviceClasses`);
        return response.data;
    }
    /**
     * Get all the devices of a project for the specified filters.
     * IMPORTANT NOTE: This API is for getting all devices for a project, not for a specific home. The result
     * will includes devices that are not linked to any home. To get only devices for a specific home, call
     * the getHomeDevices instead.
     *
     * @param projectId - The id of the project the devices belonged to
     * @param homeId  - The id of the home the devices belonged to. If not provided, return all devices
     * @param search - Filter the result by this search text. If not provided, return all
     * @param deviceClass - Filter the result by this device class
     * @param skip - Skip the 'skip' number of result. Use for paging.
     * @param limit - Return only 'limit' number of result. If not provided, return all
     */
    async getProjectDevices(projectId, search, deviceClass, skip, limit) {
        const response = await this.sendRequest(RequestMethod.GET, '/devices', {
            projectId, search, deviceClass, skip, limit,
        });
        if (response.data instanceof Array) {
            const range = parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]);
            return {
                range,
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get the device data by the specified deviceId
     * @param projectId - The id of the project the devices belonged to
     * @param deviceId - The id of the device which we want to get
     */
    async getProjectDeviceById(projectId, deviceId) {
        const response = await this.sendRequest(RequestMethod.GET, `/devices/${deviceId}`, {
            projectId,
        });
        return response.data;
    }
    /**
     * Create a project device.
     * @param projectId - The id of the project the devices belonged to
     * @param deviceClass - The device class to create
     */
    async createProjectDevice(projectId, deviceClass, name, tag) {
        const response = await this.sendRequest(RequestMethod.POST, '/devices', {
            projectId, deviceClass, name, tag,
        });
        return response.data;
    }
    /**
     * Update a project device.
     * @param projectId - The id of the project the devices belonged to
     * @param deviceId - The id of the device which we want to get
     * @param updatedData - The object including fields/values you want to update. The fields that can be updated are
     *                      name, tag, and claimCode
     */
    async updateProjectDevice(projectId, deviceId, updatedData) {
        await this.sendRequest(RequestMethod.PATCH, `/devices/${deviceId}`, {
            projectId, ...updatedData,
        });
    }
    /**
     * Delete a device from a project.
     * @param deviceId - The id of the device we want to delete
     */
    async deleteProjectDevice(deviceId) {
        await this.sendRequest(RequestMethod.DELETE, `/devices/${deviceId}`);
    }
    /**
     * Get Messages logs for a specific device
     * @param deviceId - The device we want to get the message log for
     * @param begin - The first date of the log entries we want to get
     * @param end - The last date of the log entries we want to get
     * @param limit - OPTIONAL - The maximum number of entries we want to get
     */
    async getDeviceMessageLogEntries(deviceId, begin, end, limit) {
        const response = await this.sendRequest(RequestMethod.GET, `devices/${deviceId}/logEntries`, {
            begin, end, maxEntries: limit,
        });
        return this.reformatMessageLogEntries(response.data);
    }
    /**
     * Get HTTP requests logs for a specific device
     * @param deviceId - The device we want to get the http request logs for
     * @param begin - The first date of the log entries we want to get
     * @param end - The last date of the log entries we want to get
     * @param limit - OPTIONAL - The maximum number of entries we want to get
     */
    async getDeviceHttpLogEntries(deviceId, begin, end, limit) {
        const response = await this.sendRequest(RequestMethod.GET, `devices/${deviceId}/httpLogEntries`, {
            begin, end, maxEntries: limit,
        });
        return response.data;
    }
    /**
     * Get all log requests for a specific device with the specified filters
     * @param deviceId - The device we want to get the log request for
     * @param sortBy - The name and sort order of the field we want to sort the result by. e.g. 'status:desc'
     * @param statuses - The commas separated list of status we want to filter by e.g. 'success,cancelled,failure'
     * @param skip - The index of the first entry (used for paging)
     * @param limit - The maximum number of entries we want (used for paging)
     */
    async getDeviceLogRequestEntries(deviceId, sortBy, statuses, skip, limit) {
        const response = await this.sendRequest(RequestMethod.GET, `devices/${deviceId}/logRequests`, {
            skip, limit, statuses, sortBy,
        });
        if (response.data instanceof Array) {
            return {
                range: parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]),
                items: this.fixLogRequestData(response.data),
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Create a new log request for a specific device
     * @param deviceId - The device we want to create the log request for
     * @param startDate - OPTIONAL - The start date of the log file
     * @param endDate - OPTIONAL - The end date of the log file
     * @param prefixes - OPTIONAL - The list of prefixes of the log file names
     */
    async createDeviceLogRequest(deviceId, startDate, endDate, prefixes) {
        const response = await this.sendRequest(RequestMethod.POST, `devices/${deviceId}/logRequests`, {
            start: startDate, end: endDate, prefixes,
        });
        return response.data;
    }
    /**
     * Cancel a specific log request for a device.
     * @param deviceId - The device id of the device the log request belonged to
     * @param requestId - The id of the log request we want to cancel
     */
    async cancelDeviceLogRequest(deviceId, requestId) {
        await this.sendRequest(RequestMethod.DELETE, `devices/${deviceId}/logRequests/${requestId}`);
    }
    /**
     * Get Messages logs for a specific home
     * @param userId - The user we want to get the message log for
     * @param begin - The first date of the log entries we want to get
     * @param end - The last date of the log entries we want to get
     * @param limit - OPTIONAL - The maximum number of entries we want to get
     */
    async getUserMessageLogEntries(userId, begin, end, limit) {
        const response = await this.sendRequest(RequestMethod.GET, `users/${userId}/logEntries`, {
            begin, end, maxEntries: limit,
        });
        return this.reformatMessageLogEntries(response.data);
    }
    /**
     * Get HTTP requests logs for a specific home
     * @param homeId - The home we want to get the http request logs for
     * @param begin - The first date of the log entries we want to get
     * @param end - The last date of the log entries we want to get
     * @param limit - OPTIONAL - The maximum number of entries we want to get
     */
    async getUserHttpLogEntries(userId, begin, end, limit) {
        const response = await this.sendRequest(RequestMethod.GET, `users/${userId}/httpLogEntries`, {
            begin, end, maxEntries: limit,
        });
        return response.data;
    }
    /**
     * Get Messages logs for a specific home
     * @param homeId - The home we want to get the message log for
     * @param begin - The first date of the log entries we want to get
     * @param end - The last date of the log entries we want to get
     * @param limit - OPTIONAL - The maximum number of entries we want to get
     */
    async getHomeMessageLogEntries(homeId, begin, end, limit) {
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/logEntries`, {
            begin, end, maxEntries: limit,
        });
        return this.reformatMessageLogEntries(response.data);
    }
    /**
     * Get HTTP requests logs for a specific home
     * @param homeId - The home we want to get the http request logs for
     * @param begin - The first date of the log entries we want to get
     * @param end - The last date of the log entries we want to get
     * @param limit - OPTIONAL - The maximum number of entries we want to get
     */
    async getHomeHttpLogEntries(homeId, begin, end, limit) {
        const response = await this.sendRequest(RequestMethod.GET, `homes/${homeId}/httpLogEntries`, {
            begin, end, maxEntries: limit,
        });
        return response.data;
    }
    /**
     * Get agent logs for a specific project
     * @param projectId - The project we want to get the http request logs for
     * @param begin - The first date of the log entries we want to get
     * @param end - The last date of the log entries we want to get
     * @param limit - OPTIONAL - The maximum number of entries we want to get
     * @param agentId - OPTIONAL - Filter by agent id
     */
    async getAgentMessageLogEntries(projectId, begin, end, limit, agentId) {
        const endpoint = agentId ? `projects/${projectId}/agents/${agentId}/logEntries` : `projects/${projectId}/logEntries`;
        const response = await this.sendRequest(RequestMethod.GET, endpoint, {
            agentId, begin, end, maxEntries: limit,
        });
        return this.reformatMessageLogEntries(response.data);
    }
    /**
     * Get all bundles from a project and optionally filter by deviceClass
     */
    async getProjectFirmwareBundles(projectId, deviceClass) {
        const response = await this.sendRequest(RequestMethod.GET, 'bundles', {
            projectId,
            deviceClass,
        });
        return response.data;
    }
    /**
     * Get all channels from a project and optionally filter by deviceClass
     */
    async getProjectFirmwareChannels(projectId, deviceClass) {
        const response = await this.sendRequest(RequestMethod.GET, 'channels', {
            projectId,
            deviceClass,
        });
        return response.data;
    }
    /**
     * Get multiple metric subscriptions for a project with the provided filters
     * @param projectId
     * @returns
     */
    async getMetricsReportSubscriptions(filters) {
        const response = await this.sendRequest(RequestMethod.GET, '/metricsReportSubscriptions', filters);
        if (response.data instanceof Array) {
            const range = parseDataRange(response.headers[DATA_RANGE_HEADER_KEY]);
            return {
                range,
                items: response.data,
            };
        }
        return {
            range: {
                start: 0, end: 0, total: 0,
            },
            items: [],
        };
    }
    /**
     * Get 1 metric subscription
     * @param projectId
     * @returns
     */
    async getMetricsReportSubscriptionById(subscriptionId) {
        const response = await this.sendRequest(RequestMethod.GET, `/metricsReportSubscriptions/${subscriptionId}`);
        return response.data;
    }
    /**
     * Create a metrics report subscription
     */
    async createMetricsReportSubscription(params) {
        const response = await this.sendRequest(RequestMethod.POST, '/metricsReportSubscriptions', params);
        return response.data;
    }
    async updateMetricsReportSubscription(subId, params) {
        const response = await this.sendRequest(RequestMethod.PATCH, `/metricsReportSubscriptions/${subId}`, params);
        return response.data;
    }
    /**
     * Delete a subscription by ID
     * @param projectId
     * @param subscriptionId
     */
    async deleteMetricsReportSubscription(subscriptionId) {
        await this.sendRequest(RequestMethod.DELETE, `/metricsReportSubscriptions/${subscriptionId}`);
    }
    /** ****************************   PRIVATE FUNCTIONS   ************************ */
    reformatMessageLogEntries(entries) {
        if (entries) {
            return entries.map((entry) => {
                return {
                    timestamp: entry.timestamp,
                    level: entry.level,
                    text: entry.text,
                    tags: entry.tags
                        ? entry.tags.map((tag) => {
                            const parts = tag.split(':');
                            return {
                                type: parts[0],
                                id: Number(parts[1]),
                            };
                        })
                        : [],
                    caller: entry.caller,
                    source: entry.source,
                };
            });
        }
        return [];
    }
    /**
     * Log request data contains bad start/end date, "0001-01-01T00:00:00Z", if start/end dates are not specified when creating a request.
     * Therefore, this function is to fix the start/end dates if they are invalid.
     */
    fixLogRequestData(entries) {
        if (entries) {
            return entries.map((entry) => {
                return {
                    ...entry,
                    filter: {
                        ...entry.filter,
                        start: entry.filter && entry.filter.start && Date.parse(entry.filter.start) > 0 ? entry.filter.start : undefined,
                        end: entry.filter && entry.filter.end && Date.parse(entry.filter.end) > 0 ? entry.filter.end : undefined,
                    },
                };
            });
        }
        return entries;
    }
    /**
     * We have a few API call that log the agent in and return the login info. Instead of writing the same code to handle the
     * response in many places, this function will take care of handling the response for all of them
     */
    async handleLoginResponse(response) {
        // Make sure the response has all the necessary data
        if (response.data && response.data.agentId && response.data.ttl && response.data.authenticated
            && response.headers && response.headers[XSRF_GUARD_KEY.toLocaleLowerCase()]) {
            const loginInfo = {
                authenticated: response.data.authenticated,
                agentId: response.data.agentId,
                ttl: response.data.ttl,
                orgId: response.data.orgId,
                auth2FToken: response.data.auth2FToken,
                xsrfGuard: response.headers[XSRF_GUARD_KEY.toLowerCase()],
            };
            this.xsrfGuard = loginInfo.xsrfGuard;
            return loginInfo;
        }
        // Bad response. Use 500 status code
        return Promise.reject(new ApiError(ApiErrorCode.INVALID_RESPONSE, 500, 'Invalid response'));
    }
    /**
     * Similar to the handleLoginResponse except that Login with external service's response is different so we need this function
     * to handle it. This is the response from back end
     *      {
     *          agentId
     *          orgId
     *          ttl
     *          authenticated
     *      }
     *
     * @param response
     */
    async handleExternalLoginResponse(response) {
        // Make sure the response has all the necessary data
        if (response.data && response.data.agentId && response.headers && response.headers[XSRF_GUARD_KEY.toLocaleLowerCase()]) {
            const loginInfo = {
                authenticated: true,
                agentId: response.data.agentId,
                orgId: response.data.orgId,
                ttl: response.data.ttl,
                xsrfGuard: response.headers[XSRF_GUARD_KEY.toLowerCase()],
            };
            this.xsrfGuard = loginInfo.xsrfGuard;
            return loginInfo;
        }
        // Bad response. Use 500 status code
        return Promise.reject(new ApiError(ApiErrorCode.INVALID_RESPONSE, 500, 'Invalid response'));
    }
    /**
     * Intercept all API requests and do something with the request before sending it to the server
     */
    onFulfilledRequest(config) {
        if (this.xsrfGuard && config.headers) {
            // If we have the authentication token, add it to the header.
            // eslint-disable-next-line no-param-reassign
            config.headers[XSRF_GUARD_KEY] = this.xsrfGuard;
        }
        return config;
    }
    onRejectedRequest(error) {
        return Promise.reject(this.normalizeError(error));
    }
    /**
     * Intercept all API responses and do something with the response before passing it to the requester
     */
    onFulfilledResponse(response) {
        return response;
    }
    onRejectedResponse(error) {
        const ritzError = this.normalizeError(error);
        if (ritzError.code === ApiErrorCode.UNAUTHENTICATED_CLIENT) {
            // This error code is a special case. We usually make API call to endpoints that requires the agent to be authenticated when we believe
            // the agent has authenticated. If this api call return UNAUTHENTICATED_CLIENT, that mean we think the agent is logged in but the backend
            // doesn't think the agent is logged in. This usually mean the agent has been logged out somehow or session has expired. We need to clear
            // the agent login info.
            this.clearXsrfGuard();
        }
        return Promise.reject(ritzError);
    }
}
